/**
 * Super-admin-only modal to edit the platform's Stripe fee config
 * (percentage rate + fixed per-transaction fee + currency + enabled).
 *
 * Mounted by `EditEventDialog` when a super admin clicks the pencil
 * icon next to the live transaction-fee preview. The actual gating is
 * also enforced server-side; this modal trusts that the caller already
 * passed the role check.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  StripeFeeConfig,
  useUpdateStripeFeeConfig,
} from '@/api/extensions/useStripeFeeConfig';
import { useToast } from '@/contexts/ToastContext';

interface StripeFeeConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: StripeFeeConfig;
}

export function StripeFeeConfigModal({
  open,
  onOpenChange,
  config,
}: StripeFeeConfigModalProps) {
  const { showSuccess, showError } = useToast();
  const updateMutation = useUpdateStripeFeeConfig();

  const [percentInput, setPercentInput] = useState('');
  const [fixedInput, setFixedInput] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (open) {
      // Display percentage to the user as e.g. 2.9 (not 0.029).
      setPercentInput(String((config.percentage_rate * 100).toFixed(4).replace(/\.?0+$/, '')));
      setFixedInput(String((config.fixed_fee_cents / 100).toFixed(2)));
      setCurrency(config.currency);
      setEnabled(config.enabled);
    }
  }, [open, config]);

  const handleSave = async () => {
    const pctNumber = parseFloat(percentInput);
    const fixedDollars = parseFloat(fixedInput);

    if (!Number.isFinite(pctNumber) || pctNumber < 0 || pctNumber >= 100) {
      showError('Invalid percentage', 'Enter a number between 0 and 100.');
      return;
    }
    if (!Number.isFinite(fixedDollars) || fixedDollars < 0) {
      showError('Invalid fixed fee', 'Enter a non-negative dollar amount.');
      return;
    }
    if (!/^[A-Za-z]{3}$/.test(currency)) {
      showError('Invalid currency', 'Use a 3-letter ISO code like USD.');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        percentage_rate: pctNumber / 100,
        fixed_fee_cents: Math.round(fixedDollars * 100),
        currency: currency.toUpperCase(),
        enabled,
      });
      showSuccess('Stripe fee config updated');
      onOpenChange(false);
    } catch (err: any) {
      showError(
        'Update failed',
        err?.message || 'Could not save the platform fee configuration.',
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Stripe Fee Configuration</DialogTitle>
        <DialogDescription>
          Platform-wide Stripe pricing used to calculate the transaction fee
          on every event registration. Changes apply to all new fee previews
          immediately and are honored on the next charge.
        </DialogDescription>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="stripe_pct">Percentage rate (%)</Label>
            <Input
              id="stripe_pct"
              type="number"
              min={0}
              max={99.99}
              step={0.01}
              value={percentInput}
              onChange={(e) => setPercentInput(e.target.value)}
              placeholder="2.9"
            />
            <p className="text-xs text-muted-foreground">
              Stripe's standard US card rate is 2.9%.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stripe_fixed">Fixed per-transaction fee ($)</Label>
            <Input
              id="stripe_fixed"
              type="number"
              min={0}
              step={0.01}
              value={fixedInput}
              onChange={(e) => setFixedInput(e.target.value)}
              placeholder="0.30"
            />
            <p className="text-xs text-muted-foreground">
              Stripe's standard US card fixed fee is $0.30.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="stripe_currency">Currency</Label>
            <Input
              id="stripe_currency"
              value={currency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              placeholder="USD"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Enable transaction fee on registrations
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
